import { errorToast } from "@/v2-ui/toast"
import {
  ButtonHint,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import type { ConfirmButtonProps } from "@/v2-backoffice-client/components/button"
import { useLayerAuthoringPromoteMutation } from "@/v2-backoffice-client/layer/authoring/layer.authoring.mutation"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { AUTHORING_MIRROR_ENVIRONMENT } from "@/v2-backoffice-client/feature/feature.constants"

export type LayerAuthoringPromoteButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  featureCode: string
}

/**
 * Copies the layer's dev catalog row over prod ("push to production"),
 * category memberships and sublayers included; the definition ships with
 * the next catalog publish. Deliberately does NOT touch customer
 * visibility — a layer new to prod arrives unpublished in prod's review
 * queue (see LayerReviewPublishButton for the visibility half of the
 * story). The hint also says what this is NOT: the way to get a dev edit
 * live. Dev edits are live the moment they save, and an operator who has
 * just watched the Publish button disappear after publishing reads the one
 * push-shaped button left as exactly that (2026-09-08).
 */
function LayerAuthoringPromoteButton(props: LayerAuthoringPromoteButtonProps) {
  const { featureCode, children, ...buttonProps } = props

  const [promote, { loading, error }] = useLayerAuthoringPromoteMutation({
    refetchQueries: [LAYER_AUTHORING_STATUSES_QUERY_NAME],
  })
  errorToast(error, { title: "Failed to push to production" })

  return (
    <ConfirmButton
      variant="success"
      isStacked
      iconSrc="rocket"
      content={
        <ButtonHint title="Push prod — copy the definition to the production catalog">
          Copies this layer&apos;s catalog definition — configuration, filters,
          texts, categories and sublayers — to the production catalog. It ships
          with the next &quot;Publish catalog → prod&quot; run, and a layer new
          to prod then waits hidden in prod&apos;s review queue until Go live
          there. Not needed for dev: every edit in this backoffice is live in
          dev the moment it saves.
        </ButtonHint>
      }
      confirmContent="Copy this layer's definition to the production catalog? Customers see it only after a 'Publish catalog → prod' run and Go live in prod's review queue."
      isLoading={loading}
      onConfirm={() => {
        promote({
          variables: {
            input: {
              FeatureCode: featureCode,
              FromEnvironment: AUTHORING_MIRROR_ENVIRONMENT,
              ToEnvironment: "prod",
            },
          },
        })
      }}
      {...buttonProps}
    >
      {children ?? "Push prod"}
    </ConfirmButton>
  )
}

export default LayerAuthoringPromoteButton
